<template>
  <Dialog
    :visible="show"
    modal
    :draggable="false"
    :header="t('botEditor.codeEditor.title')"
    :style="{ width: '90vw', maxWidth: '72rem', height: '83vh' }"
    content-class="flex flex-col"
    :pt="{ title: { class: 'select-none' } }"
    @update:visible="(visible) => { if (!visible) close(); }"
  >
    <!-- Sezione Librerie -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-2 select-none">{{ t('botEditor.codeEditor.librariesTitle') }}</h3>
      <div class="flex gap-x-6 gap-y-2 flex-wrap">
        <div v-for="lib in availableLibs" :key="lib.id" class="flex items-center gap-2">
          <Checkbox v-model="selectedLibs" :value="lib.id" :input-id="libInputId(lib.id)" />
          <label :for="libInputId(lib.id)" class="flex items-center gap-2 cursor-pointer select-none">
            <span class="text-white">{{ lib.name }}</span>
            <span class="text-gray-400 text-sm">({{ t(lib.descKey) }})</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Editor di Codice -->
    <!--
      Focus: once its opening animation ends, the Dialog focuses the first
      [autofocus] element it finds (the × button if there is none), which would
      take the focus away from the editor. This wrapper is that element and
      hands the focus straight on to the editor.
      Escape: CodeMirror uses it to close its autocomplete / search panel or to
      collapse a selection, and then prevents the default. Those presses stop
      here so they don't also close the dialog (and discard the code).
    -->
    <div
      class="code-editor-wrap flex-grow border border-gray-700 rounded-lg overflow-hidden"
      tabindex="-1"
      autofocus
      @focus="focusEditor"
      @keydown.esc="stopEscapeHandledByEditor"
    >
      <Codemirror
        v-model="currentCode"
        :placeholder="t('botEditor.codeEditor.placeholder')"
        :style="{ height: '100%' }"
        :autofocus="true"
        :indent-with-tab="true"
        :tab-size="2"
        :extensions="extensions"
        @ready="onEditorReady"
      />
    </div>
    <div class="text-xs text-gray-400 mt-2">
      <p>{{ t('botEditor.codeEditor.helpLine1') }}</p>
      <p>{{ t('botEditor.codeEditor.helpLine2') }} </p>
    </div>

    <template #footer>
      <div class="flex-1 text-sm text-red-400">
        <span v-if="syntaxError">{{ syntaxError }}</span>
      </div>
      <Button severity="secondary" :label="t('common.cancel')" @click="close" />
      <Button :label="t('botEditor.codeEditor.saveButton')" @click="save" />
    </template>
  </Dialog>
</template>

<script setup>
/**
 * CodeEditorModal.vue
 *
 * JavaScript editor dialog of the flow editor's "execute code" node: a
 * CodeMirror editor plus the sandbox libraries to inject. Save runs a syntax
 * check (compile only, nothing is executed) and, if it passes, emits the code
 * and the selected libraries, then closes.
 * Cancel, the × button and Escape all close it without
 * saving. Escape presses CodeMirror uses itself (closing autocomplete or the
 * search panel, collapsing a selection) don't close the dialog.
 * Code and libraries are reset from the initial props every time it opens.
 *
 * @example
 * <CodeEditorModal
 *   :show="isCodeEditorModalOpen"
 *   :initial-code="node.data.code"
 *   :initial-libs="node.data.libs"
 *   @close="onCodeEditorClose"
 *   @save="saveCodeFromModal"
 * />
 */
import { ref, watch } from 'vue';
import { Codemirror } from 'vue-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  /** Whether the dialog is visible */
  show: {
    type: Boolean,
    default: false,
  },
  /** Code loaded into the editor on open */
  initialCode: {
    type: String,
    default: '',
  },
  /** Library ids selected on open */
  initialLibs: {
    type: Array,
    default: () => [],
  },
});

/**
 * @event save - Code passed the syntax check; payload `{ code, libs }` (libs = selected library ids). Always followed by `close`.
 * @event close - Dialog closed (Cancel, ×, Escape, or after a save)
 */
const emit = defineEmits(['close', 'save']);

const currentCode = ref('');
const selectedLibs = ref([]);
const syntaxError = ref(null);
const { t } = useI18n();

// Libraries the sandbox can inject: `id` is what node.data.libs stores, `name`
// is a proper noun (not translated), `descKey` the translated description.
const availableLibs = [
  { id: 'lodash', name: 'Lodash', descKey: 'botEditor.codeEditor.libs.lodash.desc' },
  { id: 'date-fns', name: 'Date-fns', descKey: 'botEditor.codeEditor.libs.dateFns.desc' },
  { id: 'nanoid', name: 'Nano ID', descKey: 'botEditor.codeEditor.libs.nanoid.desc' },
  { id: 'crypto', name: 'Crypto', descKey: 'botEditor.codeEditor.libs.crypto.desc' },
];

const libInputId = (libId) => `code-editor-lib-${libId}`;

const extensions = [javascript(), oneDark];

// The CodeMirror view of the editor currently mounted (a new one on every open)
let editorView = null;

const onEditorReady = ({ view }) => {
  editorView = view;
};

const focusEditor = () => {
  editorView?.focus();
};

const stopEscapeHandledByEditor = (event) => {
  if (event.defaultPrevented) event.stopPropagation();
};

watch(() => props.show, (newVal) => {
  if (newVal) {
    currentCode.value = props.initialCode || '';
    selectedLibs.value = props.initialLibs ? [...props.initialLibs] : [];
    syntaxError.value = null;
  }
});

const close = () => {
  emit('close');
};

const save = () => {
  // Simple inline syntax check only when saving
  if (currentCode.value && currentCode.value.trim()) {
    try {
      // eslint-disable-next-line no-new-func
      new Function(`(async function() { ${currentCode.value} })`);
    } catch (e) {
      syntaxError.value = e?.message || t('botEditor.codeEditor.cannotSaveWithSyntaxError');
      return;
    }
  }

  emit('save', {
    code: currentCode.value,
    libs: selectedLibs.value,
  });
  close();
};
</script>

<style scoped>
/* Only this dialog's editor: the other CodeMirror editors keep oneDark's own
   colours. There is no .cm-gutters rule: oneDark's theme-class-prefixed gutter
   rule outranks a bare .cm-gutters, so the gutter has always kept oneDark's
   styling here, and a wrapper-prefixed rule would now beat it and change it. */
.code-editor-wrap :deep(.cm-editor) {
  background-color: #2d3748; /* gray-800 */
  color: white;
  height: 100%;
}
</style>
